import React, { useState, useRef } from 'react';
import uploadCloud from 'img/upload-cloud.svg';
import 'css/components/upload-box.css';
import DismissButton from 'components/dismiss-button';
import { ProgressCircle } from 'components/progress-circle';
import checkAndCircleGreen from 'img/check-and-circle-green.svg';
import pIcon from 'img/p.svg';
import uploadFile from 'img/upload-file.svg';

interface UploadBoxProps {
  name: string;
  acceptedFileTypes: string[];
  acceptedFileExtensionsText: string;
  isActive?: boolean; // For the supplementary documents upload box
  maxFiles?: number;
  onFileUpload: (file: File) => Promise<unknown> | void;
  optional?: boolean;
  isUploading?: boolean;
  onLimitExceeded?: (rejected: File[]) => void;
  disabled?: boolean;
  onDismiss?: () => Promise<void> | void;
  /**
   * Set when the server rejected the upload. Without it this box says
   * "Uploaded successfully!" purely because the right NUMBER of files was
   * picked — it has no idea whether the upload worked. A teacher whose lesson
   * failed with a 500 was being shown "Uploaded successfully!" with the error
   * printed underneath it.
   *
   * Optional, so the callers that do not pass it behave exactly as before.
   */
  failed?: boolean;
}

const UploadBox = ({
  name,
  acceptedFileTypes,
  acceptedFileExtensionsText,
  isActive = true,
  maxFiles = 1,
  onFileUpload,
  optional = false,
  disabled = false,
  isUploading = false,
  onDismiss,
  failed = false
}: UploadBoxProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0); // NEW
  const dragCounter = useRef(0);
  const inputId = `${name}-upload`;
  const fileCount = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const remainingSlots = Math.max(0, maxFiles - fileCount.current);
  const atLimit = remainingSlots === 0;
  const busy = disabled || uploadingCount > 0;

  const acceptFiles = (incoming: File[]) => {
    if (!incoming.length || atLimit) return;

    const allowed = incoming.slice(0, remainingSlots);
    // const rejected = incoming.slice(remainingSlots);

    setFiles(prev => [...prev, ...allowed]);
    setUploadingCount(c => c + allowed.length);

    const tasks = allowed.map((f) =>
      Promise.resolve(onFileUpload?.(f))
        .finally(() => setUploadingCount(c => c - 1))
        .then(() => { fileCount.current++; })
    );

    // Fire-and-forget; UI reacts to uploadingCount
    Promise.allSettled(tasks).then(() => setShowTooltip(true));
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (busy || !isActive) return;
    e.preventDefault(); e.stopPropagation();
    dragCounter.current++;
    setIsDragging(true);
    setShowTooltip(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (busy || !isActive) return;
    e.preventDefault(); e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setShowTooltip(false);
    }
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (busy || !isActive) return;
    e.preventDefault(); e.stopPropagation();
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (busy || !isActive) return;
    e.preventDefault(); e.stopPropagation();
    dragCounter.current = 0;
    setIsDragging(false);
    acceptFiles(Array.from(e.dataTransfer.files || []));
  };
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (busy || !isActive) return;
    const newFiles = Array.from(e.target.files || []);
    acceptFiles(newFiles);
    e.currentTarget.value = '';
  };

  // Note this only means "the expected number of files has been picked". It is
  // not a statement about whether the upload succeeded, which is why `failed`
  // has to be supplied by the caller that actually knows.
  const allUploaded = fileCount.current === maxFiles;
  const resetUploadBox = async () => {
    // Avoid corrupting the uploading counter while uploads are in-flight
    if (uploadingCount > 0) {
      setShowTooltip(false);
      return;
    }
    // Allow parent to handle side effects (like deleting a created lesson)
    try { await onDismiss?.() } catch {}
    setFiles([]);
    fileCount.current = 0;
    setIsDragging(false);
    setShowTooltip(false);
    dragCounter.current = 0;
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="upload-box-container">
      <div
        className={`upload-box ${isDragging ? 'drag-over' : ''} ${allUploaded ? 'has-file' : ''} ${!isActive ? 'inactive' : ''} ${busy || atLimit ? 'disabled' : ''}`}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        aria-busy={busy}
        aria-disabled={busy || atLimit}
      >
        {optional && <div className="optional-badge">OPTIONAL</div>}

        <input
          type="file"
          id={inputId}
          accept={acceptedFileTypes.join(',')}
          onChange={handleFileSelect}
          multiple={remainingSlots > 1}
          style={{ display: 'none' }}
          disabled={busy || atLimit}
          ref={fileInputRef}
        />

        <label htmlFor={inputId} className="upload-button" style={{ pointerEvents: busy || atLimit ? 'none' : undefined }}>
          <img src={uploadCloud} className="upload-icon" alt="Upload" />
          Upload {name}
        </label>

        <div className="file-types-text">{acceptedFileExtensionsText}</div>
        <div className="drag-text">
          {busy
            ? <ProgressCircle />
            : atLimit
              ? `Max files uploaded (${maxFiles})`
              : `Or drag file here to upload (${maxFiles - fileCount.current} remaining)`}
        </div>

        {allUploaded && !failed && <div className="upload-box-success">Uploaded successfully!</div>}
        {allUploaded && failed && <div className="upload-box-error">Upload failed — see the message below.</div>}
      </div>

      <div className={`tooltip-row ${showTooltip ? 'visible' : ''}`}>
        {files.length === 0 ? (
          <div className="drag-tooltip">
            <img src={uploadFile} className="upload-icon" alt="Upload" />
            Drop file here to upload
          </div>
        ) : (
          <div className="uploaded-files-tooltip">
            <div className="uploaded-files-header">
              <DismissButton onClick={resetUploadBox} />
            </div>
            <div className="uploaded-files-content">
              {files.map((file, index) => (
                <div className="file-info" key={`${file.name}-${file.size}-${index}`}>
                  <img src={pIcon} className="upload-icon" alt="PPT" />
                  <span>{file.name}</span>
                  <img src={checkAndCircleGreen} className="upload-icon" alt="Success" />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default UploadBox;